import { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { Analytics } from '@vercel/analytics/react'
import './index.css'
import App from './App.tsx'
import { initPlatformEngine } from './platform/init'
import { PlatformProvider } from './platform/context'
import { ThemeProvider } from './contexts/ThemeContext'
import { initSentry } from './lib/sentry'
import { installStaleChunkReload, isStaleChunkError, reloadOnceForStaleChunk } from './lib/staleChunkReload'
import { installTouchDragData } from './lib/touchDragData'

// Initialize Sentry as early as possible so bootstrap errors (platform engine
// init, localStorage cleanup, etc.) are captured. No-op in test envs and when
// VITE_SENTRY_DSN is not set.
initSentry()
installStaleChunkReload()
installTouchDragData()

// ============================================================================
// Error boundary: Sentry-aware so unhandled render errors are reported with
// the workspace / user context set by SentryContextSync.
// ============================================================================
function RootErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  const staleChunk = isStaleChunkError(error)
  // A tab from before the latest deploy asked for a chunk that no longer exists.
  // Re-rendering cannot fix that; loading the new build can (lib/staleChunkReload).
  const [reloading, setReloading] = useState(false)
  useEffect(() => {
    if (staleChunk && reloadOnceForStaleChunk()) setReloading(true)
  }, [staleChunk])
  const message = reloading
    ? 'A newer version of the app was released while this page was open. Loading it now…'
    : error instanceof Error ? error.message : String(error)
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'system-ui, sans-serif',
      padding: '2rem',
      background: '#fef2f2',
      color: '#991b1b',
    }}>
      <div style={{ maxWidth: '600px' }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Something went wrong</h1>
        <p style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
          The error has been reported. Please refresh the page.
        </p>
        <pre style={{ overflow: 'auto', fontSize: '0.875rem', marginTop: '1rem', whiteSpace: 'pre-wrap' }}>
          {message}
        </pre>
        <button
          onClick={staleChunk ? () => window.location.reload() : resetError}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            background: '#991b1b',
            color: '#fff',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// One-time localStorage cleanup
// Removes stale data from old localStorage persistence (now disabled)
// Version-gated so it only runs once per browser
// ============================================================================
const STORAGE_CLEANUP_VERSION = 'v1-supabase-only';
const CLEANUP_VERSION_KEY = 'peak-priorities-storage-cleanup-version';

function clearStaleLocalStorage() {
  const currentCleanupVersion = localStorage.getItem(CLEANUP_VERSION_KEY);
  
  if (currentCleanupVersion === STORAGE_CLEANUP_VERSION) {
    // Already cleaned up
    return;
  }
  
  console.log('[Storage Cleanup] Clearing stale localStorage data...');
  
  // List of all old localStorage keys from previous persist middleware
  const keysToRemove = [
    'attribute-governance-store',
    'peak-priorities-value-proposals',
    'enterprise-identity-storage',
    'attributes-storage',
    'cycle-participation-storage',
    'user-storage',
    'peak-priorities-audit',
    'peak-priorities-sessions',
    'criteria-storage',
    'peak-priorities-clusters',
    'prioritization-comments',
    'prioritization-demand-clusters',
    'prioritization-clarifications',
    'prioritization-item-relations',
    'groups-storage',
    'roles-storage',
  ];
  
  let removedCount = 0;
  for (const key of keysToRemove) {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
      removedCount++;
    }
  }
  
  // Mark cleanup as complete
  localStorage.setItem(CLEANUP_VERSION_KEY, STORAGE_CLEANUP_VERSION);
  
  console.log(`[Storage Cleanup] Removed ${removedCount} stale localStorage entries`);
}

// Run cleanup on app load
clearStaleLocalStorage();

function renderApp() {
  const rootEl = document.getElementById('root')
  if (!rootEl) {
    document.body.innerHTML = '<div style="padding:2rem;font-family:system-ui"><h1>Error</h1><p>Root element #root not found.</p></div>'
    return
  }

  createRoot(rootEl).render(
    <Sentry.ErrorBoundary fallback={RootErrorFallback}>
      <ThemeProvider>
        <PlatformProvider>
          <App />
          <Analytics />
        </PlatformProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>,
  )
}

try {
  initPlatformEngine()
  renderApp()
} catch (err) {
  const rootEl = document.getElementById('root')
  const message = err instanceof Error ? err.message : String(err)
  const stack = err instanceof Error ? err.stack : ''
  console.error('Bootstrap error:', err)
  if (rootEl) {
    rootEl.innerHTML = `
      <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui;padding:2rem;background:#fef2f2;color:#991b1b">
        <div style="max-width:600px">
          <h1 style="font-size:1.25rem;margin-bottom:0.5rem">Failed to start app</h1>
          <pre style="overflow:auto;font-size:0.875rem;margin-top:1rem;white-space:pre-wrap">${message}</pre>
          ${stack ? `<pre style="overflow:auto;font-size:0.75rem;margin-top:0.5rem;white-space:pre-wrap;color:#b91c1c">${stack}</pre>` : ''}
          <p style="margin-top:1rem;font-size:0.875rem">Check the console for details.</p>
        </div>
      </div>
    `
  }
}
